import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Typography
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import User1 from 'assets/images/users/user-round.svg';
import { getApi } from 'views/services/api';

// assets
import { IconLogout, IconSettings } from '@tabler/icons';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);

  // Obtener datos del usuario actual del localStorage
  const currentUser = JSON.parse(localStorage.getItem('hotelData'));
  console.log('Current user data:', currentUser);

  // Estados para datos específicos
  const [hotelData, setHotelData] = useState(null);
  const [userDisplayName, setUserDisplayName] = useState('');

  //handle logout--------------------------------------------
  const handleLogout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('hotelData');
    navigate('/login');
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  // Función para obtener datos del hotel (solo si el usuario es HotelAdmin)
  const fetchHotelData = async () => {
    if (currentUser?.role === 'HotelAdmin' && currentUser?.hotelId) {
      try {
        const response = await getApi(`api/hotel/view/${currentUser.hotelId}`);
        setHotelData(response?.data);
      } catch (error) {
        console.log('Error fetching hotel data:', error);
      }
    }
  };

  // Función para determinar el nombre a mostrar
  const determineDisplayName = () => {
    if (!currentUser) {
      setUserDisplayName('Usuario');
      return;
    }

    switch (currentUser.role) {
      case 'HotelAdmin':
        // Si es admin de hotel, mostrar nombre del hotel (se actualizará cuando se cargue hotelData)
        if (hotelData?.name) {
          setUserDisplayName(`Hotel ${hotelData.name.charAt(0).toUpperCase() + hotelData.name.slice(1)}`);
        } else {
          setUserDisplayName('Hotel Admin');
        }
        break;

      case 'admin':
        // Si es super admin, usar sus datos personales del localStorage
        if (currentUser.firstName && currentUser.lastName) {
          setUserDisplayName(
            `Admin ${currentUser.firstName.charAt(0).toUpperCase() + currentUser.firstName.slice(1)} ${
              currentUser.lastName.charAt(0).toUpperCase() + currentUser.lastName.slice(1)
            }`
          );
        } else {
          setUserDisplayName('Super Admin');
        }
        break;

      default:
        // Usuario normal del hotel
        if (currentUser.firstName && currentUser.lastName) {
          setUserDisplayName(
            `${currentUser.firstName.charAt(0).toUpperCase() + currentUser.firstName.slice(1)} ${
              currentUser.lastName.charAt(0).toUpperCase() + currentUser.lastName.slice(1)
            }`
          );
        } else {
          setUserDisplayName('Usuario');
        }
        break;
    }
  };

  // useEffect para cargar datos iniciales
  useEffect(() => {
    if (currentUser) {
      fetchHotelData(); // Solo hace fetch si es necesario
      determineDisplayName();
    }
  }, []);

  // useEffect para actualizar el nombre cuando se cargan los datos del hotel
  useEffect(() => {
    determineDisplayName();
  }, [hotelData]);

  // Función para manejar configuración de perfil
  const handleProfileSettings = () => {
    if (currentUser?.role === 'admin') {
      navigate('/dashboard/adminprofile');
    } else {
      navigate('/dashboard/profile');
    }
  };

  // Función para ir al dashboard apropiado
  const handleGoHome = () => {
    if (currentUser?.role === 'admin') {
      navigate('/dashboard/admindashboard');
    } else {
      navigate('/dashboard/app');
    }
  };

  return (
    <>
      <Chip
        sx={{
          height: '48px',
          alignItems: 'center',
          borderRadius: '27px',
          transition: 'all .2s ease-in-out',
          borderColor: theme.palette.primary.light,
          backgroundColor: theme.palette.primary.light,
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: theme.palette.primary.main,
            background: `${theme.palette.primary.main}!important`,
            color: theme.palette.primary.light,
            '& svg': {
              stroke: theme.palette.primary.light
            }
          },
          '& .MuiChip-label': {
            lineHeight: 0
          }
        }}
        icon={
          <Avatar
            src={User1}
            sx={{
              ...theme.typography.mediumAvatar,
              margin: '8px 0 8px 8px !important',
              cursor: 'pointer'
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            color="inherit"
          />
        }
        label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
        variant="outlined"
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
      />
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <Box sx={{ p: 2 }}>
                    <Stack>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4">Welcome Back,</Typography>
                        <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                          {userDisplayName}
                        </Typography>
                      </Stack>
                      <Typography variant="subtitle2">
                        {currentUser?.role === 'admin'
                          ? 'Super Administrator'
                          : currentUser?.role === 'HotelAdmin'
                          ? 'Hotel Administrator'
                          : 'Hotel Staff'}
                      </Typography>
                    </Stack>
                  </Box>
                  <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                    <Box sx={{ p: 2 }}>
                      <List
                        component="nav"
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          minWidth: 300,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: '10px',
                          [theme.breakpoints.down('md')]: {
                            minWidth: '100%'
                          },
                          '& .MuiListItemButton-root': {
                            mt: 0.5
                          }
                        }}
                      >
                        <ListItemButton sx={{ borderRadius: `${customization.borderRadius}px` }} onClick={handleGoHome}>
                          <ListItemIcon>
                            <IconSettings stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Home</Typography>} />
                        </ListItemButton>

                        <ListItemButton sx={{ borderRadius: `${customization.borderRadius}px` }} onClick={handleProfileSettings}>
                          <ListItemIcon>
                            <IconSettings stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Profile Settings</Typography>} />
                        </ListItemButton>

                        <ListItemButton sx={{ borderRadius: `${customization.borderRadius}px` }} onClick={handleLogout}>
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="1.3rem" />
                          </ListItemIcon>
                          <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                        </ListItemButton>
                      </List>
                    </Box>
                  </PerfectScrollbar>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default ProfileSection;
